<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BackstageButton from '../../../components/common/BackstageButton.vue';
import BackstageImage from '../../../components/common/BackstageImage.vue';
import { useAnalytics } from '../../../composables/analytics';
import { ButtonVariant } from '../../../models/button-variant.enum';
import { GaCustomEvents } from '../../../models/ga-custom-events.enum';
import { useUserStore } from '../../../stores/user';
import { bynder16by9Image } from '../../../utils/helper';
import type { ResolvedSelfSellInModel } from '../composables/useSelfSellIn';
import SectionContainer from './SectionContainer.vue';
import VideoPlayerPopup from './VideoPlayerPopup.vue';

defineProps<{ data: NonNullable<ResolvedSelfSellInModel['welcomeVideoSection']> }>();

const { t } = useI18n();
const { addCustomGaEvent } = useAnalytics();
const { selectedAccount } = storeToRefs(useUserStore());
const showVideoPopup = ref(false);

const openVideo = () => {
  showVideoPopup.value = true;
  addCustomGaEvent({
    event: GaCustomEvents.SelfSellInWelcomeVideoPlay,
    component: 'WelcomeVideo',
    account_id: selectedAccount.value.id,
    account_name: selectedAccount.value.name,
    account_country: selectedAccount.value.billingCountry,
  });
};

const closeVideoPopup = () => {
  showVideoPopup.value = false;
};
</script>

<template>
  <SectionContainer :section-title="data.title">
    <button class="video-preview" data-cy="video-preview" @click="openVideo">
      <BackstageImage
        :src="bynder16by9Image(data.video?.poster ?? '')"
        class="video-preview__image"
        :alt="data.title" />
      <h2 class="video-preview__text" data-cy="video-preview-title">{{ data.title }}</h2>
      <BackstageButton
        :variant="ButtonVariant.PRIMARY_INVERTED"
        class="video-preview__button"
        data-cy="video-preview-button"
        @click="openVideo">
        {{ t('viewSelfSellIn.welcome_video.watch_video') }}
      </BackstageButton>
    </button>
  </SectionContainer>
  <VideoPlayerPopup v-if="showVideoPopup" :videos="[data.video]" @close="closeVideoPopup" />
</template>

<style lang="scss" scoped>
.welcome-video {
  position: relative;
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.5) 100%);
    pointer-events: none;
  }
}
.video-preview {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  outline-offset: 2px;
  &__image,
  &__text,
  &__button {
    grid-column: 1;
    grid-row: 1;
  }

  &__text,
  &__button {
    z-index: 1;
  }

  &__image {
    width: 100%;
    aspect-ratio: 16 / 9;
  }

  &__text {
    font-size: 3.75rem;
    color: var(--secondary-text-color);
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: -1.2px;
    text-align: center;
    justify-self: center;
    align-self: center;
  }

  &__button {
    justify-self: start;
    align-self: end;
    margin: 24px;
  }
}
</style>
